import { api } from 'api/api';
import {
    CategoryDto,
    CreateCategoryRequest,
    UpdateCategoryRequest,
} from 'api/dtos/category';

export const categoryApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getCategoriesByEntity: builder.query<CategoryDto[], string>({
            query: (entityId) => `entity/${entityId}/categories`,
            providesTags: (result, _, entityId) => (result
                ? [
                    ...result.map((c) => ({ type: 'Category' as const, id: c.id })),
                    { type: 'Category', id: `ENTITY-${entityId}` },
                ]
                : [{ type: 'Category', id: `ENTITY-${entityId}` }]),
        }),
        getCategoryById: builder.query<CategoryDto, { entityId: string; id: string }>({
            query: ({ entityId, id }) => `entity/${entityId}/category/${id}`,
            providesTags: (result, _, { id }) => [{ type: 'Category', id }],
        }),
        createCategory: builder.mutation<CategoryDto, { entityId: string; data: CreateCategoryRequest }>({
            query: ({ entityId, data }) => ({
                url: `entity/${entityId}/category`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, _, { entityId }) => [
                'Category',
                'Event',
                { type: 'Category', id: `ENTITY-${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Kategoria została pomyślnie utworzona.',
                errorMessage: 'Nie udało się utworzyć kategorii. Spróbuj ponownie.',
            },
        }),
        updateCategory: builder.mutation<void, { entityId: string; id: string; data: UpdateCategoryRequest }>({
            query: ({ entityId, id, data }) => ({
                url: `entity/${entityId}/category/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, _, { id, entityId }) => [
                'Category',
                'Event',
                { type: 'Category', id },
                { type: 'Category', id: `ENTITY-${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Kategoria została pomyślnie zaktualizowana.',
                errorMessage: 'Nie udało się zaktualizować kategorii. Spróbuj ponownie.',
            },
        }),
        deleteCategory: builder.mutation<void, { entityId: string; id: string }>({
            query: ({ entityId, id }) => ({
                url: `entity/${entityId}/category/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, _, { id, entityId }) => [
                'Category',
                'Event',
                { type: 'Category', id },
                { type: 'Category', id: `ENTITY-${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Kategoria została pomyślnie usunięta.',
                errorMessage: 'Nie udało się usunąć kategorii. Spróbuj ponownie.',
            },
        }),
        assignCategoryToEvent: builder.mutation<void, { entityId: string; eventId: string; categoryId: string }>({
            query: ({ entityId, eventId, categoryId }) => ({
                url: `entity/${entityId}/event/${eventId}/assign/${categoryId}`,
                method: 'POST',
            }),
            invalidatesTags: (result, _, { categoryId }) => [
                { type: 'Category', id: categoryId },
            ],
            extraOptions: {
                successMessage: 'Kategoria została pomyślnie przypisana do wydarzenia.',
                errorMessage: 'Nie udało się przypisać kategorii do wydarzenia. Spróbuj ponownie.',
            },
        }),
        removeCategoryFromEvent: builder.mutation<void, { entityId: string; eventId: string; categoryId: string }>({
            query: ({ entityId, eventId, categoryId }) => ({
                url: `entity/${entityId}/event/${eventId}/category/remove/${categoryId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, _, { categoryId }) => [
                { type: 'Category', id: categoryId },
            ],
            extraOptions: {
                successMessage: 'Kategoria została pomyślnie usunięta z wydarzenia.',
                errorMessage: 'Nie udało się usunąć kategorii z wydarzenia. Spróbuj ponownie.',
            },
        }),
    }),
});

export const {
    useGetCategoriesByEntityQuery,
    useLazyGetCategoriesByEntityQuery,
    useGetCategoryByIdQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    useAssignCategoryToEventMutation,
    useRemoveCategoryFromEventMutation,
} = categoryApi;
